import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Box, CircularProgress, Alert, Button,
  TextField, Switch, FormControlLabel, List, ListItem, ListItemText,
  ListItemSecondaryAction, Checkbox, Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { notificationsApi, swarmApi } from '../api';

function Notifications() {
  const [settings, setSettings] = useState({ phone: '', enabled: false, subscriptions: [] });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, servicesRes] = await Promise.all([
        notificationsApi.getSettings(),
        swarmApi.listServices().catch(() => ({ data: [] })),
      ]);
      setSettings({
        phone: settingsRes.data.phone || '',
        enabled: settingsRes.data.enabled || false,
        subscriptions: settingsRes.data.subscriptions || [],
      });
      const names = [...new Set(servicesRes.data.map(s => s.name).filter(Boolean))];
      setServices(names);
      setError('');
    } catch (err) {
      setError('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await notificationsApi.updateSettings({
        phone: settings.phone,
        enabled: settings.enabled,
        subscriptions: settings.subscriptions,
      });
      setSuccess('Settings saved successfully');
    } catch {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      const res = await notificationsApi.sendTest();
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send test notification');
    }
  };

  const toggleSubscription = (serviceName) => {
    setSettings(prev => {
      const existing = prev.subscriptions.find(s => s.serviceName === serviceName);
      if (existing) {
        return { ...prev, subscriptions: prev.subscriptions.map(s =>
          s.serviceName === serviceName ? { ...s, smsEnabled: !s.smsEnabled } : s
        )};
      }
      return { ...prev, subscriptions: [...prev.subscriptions, { serviceName, smsEnabled: true }] };
    });
  };

  const isSubscribed = (serviceName) => {
    return settings.subscriptions.find(s => s.serviceName === serviceName)?.smsEnabled || false;
  };

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" fontWeight="bold" gutterBottom>Notification Settings</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>SMS Configuration (Twilio)</Typography>
        <FormControlLabel
          control={<Switch checked={settings.enabled} onChange={e => setSettings(p => ({ ...p, enabled: e.target.checked }))} />}
          label="Enable SMS Notifications"
        />
        <TextField
          label="Phone Number"
          fullWidth
          margin="normal"
          value={settings.phone}
          onChange={e => setSettings(p => ({ ...p, phone: e.target.value }))}
          placeholder="+1234567890"
          helperText="International format with country code (e.g., +1 for US)"
        />
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button variant="outlined" startIcon={<SendIcon />} onClick={handleTestNotification}
            disabled={!settings.phone || !settings.enabled}>
            Send Test
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Service Subscriptions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which services should trigger SMS alerts when they go down or become degraded.
        </Typography>
        <List>
          {services.map((name) => (
            <React.Fragment key={name}>
              <ListItem>
                <ListItemText primary={name} />
                <ListItemSecondaryAction>
                  <Checkbox checked={isSubscribed(name)} onChange={() => toggleSubscription(name)} />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
          {services.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>No services found to subscribe to.</Typography>
          )}
        </List>
      </Paper>
    </Container>
  );
}
export default Notifications;
